import moment, { type Moment } from "moment-timezone";
import { getStore } from "@store";
import type {
  DateInput,
  DateType,
  ConvertedISODateTime,
  TimeZoneOverride,
  TimeZoneOverrideResult,
  MachineNow,
  BeautifiedTime,
  DurationUnit,
} from "@types";

const EST_TIMEZONE = "America/New_York";

function resolveTimeZone(timeZone?: string): string {
  if (timeZone) return timeZone;
  if (window.timeZone) return window.timeZone;
  try {
    return getStore().getState().config.timeZone || EST_TIMEZONE;
  } catch {
    return EST_TIMEZONE;
  }
}

/**
 * Returns the current moment in Eastern Time (America/New_York).
 * Use this instead of bare moment() to ensure all "now" references
 * are in EST regardless of the user's system timezone.
 */
export function getESTNow(): Moment {
  return moment.tz(EST_TIMEZONE);
}

/**
 * Normalizes a date object to either a Date or Moment object
 * @param dateObj - The date object to normalize
 * @param toType - The target type ('date' or 'moment')
 * @returns The normalized date object
 */
export function normalizeDateType(
  dateObj: DateInput,
  toType: DateType = "date"
): Date | Moment | undefined {
  if (!dateObj) {
    return undefined;
  }

  const dateObjType =
    Object.prototype.toString.call(dateObj) === "[object Date]"
      ? "date"
      : moment.isMoment(dateObj)
        ? "moment"
        : undefined;

  if (toType === "date") {
    return dateObjType === "moment" ? (dateObj as Moment).toDate() : (dateObj as Date);
  } else if (toType === "moment") {
    if (dateObjType === "date") {
      return moment((dateObj as Date).toISOString());
    }
    if (typeof dateObj === "string" && moment(dateObj).isValid()) {
      return moment(dateObj);
    }
    return dateObj as Moment;
  }
  return undefined;
}

/**
 * Formats a date/time using moment with the specified format string
 * @param dateTime - The date/time to format
 * @param formatString - The format string (default: "YYYY-MM-DDTHH:mm:ss")
 * @returns The formatted date string or null if invalid
 */
export function formatDateTime(
  dateTime: DateInput,
  formatString: string = "YYYY-MM-DDTHH:mm:ss"
): string | null {
  // moment converts undefined to current datetime, but not nulls, additional verification
  if (!dateTime) {
    return null;
  }
  
  const momentDateTime = moment(dateTime);
  return momentDateTime.isValid() ? momentDateTime.format(formatString) : null;
}

/**
 * Gets the timezone abbreviation for a given timezone string
 * @param timeZoneString - The timezone string (e.g., 'America/New_York')
 * @returns The timezone abbreviation (e.g., 'EST', 'EDT')
 */
export function getTimeZoneAbbr(timeZoneString?: string): string {
  const tz = timeZoneString || window.timeZone;
  if (tz && moment.tz.zone(tz)) {
    return moment.tz(tz).format("z");
  }
  return "";
}

/**
 * Checks if a given ISO date is before the current time
 * @param givenISODate - The ISO date string to check
 * @returns True if the given date is in the past
 */
export function givenTimeIsBeforeCurrent(givenISODate: DateInput): boolean {
  if (!givenISODate) {
    return false;
  }
  const givenMoment = moment(givenISODate);
  return givenMoment.isValid() && getESTNow().isAfter(givenMoment);
}

/**
 * Checks if a given ISO date is before another date
 * @param givenISODate - The first ISO date string
 * @param other - The second ISO date string to compare against
 * @returns True if the first date is before the second date
 */
export function givenTimeIsBeforeOther(givenISODate: DateInput, other: DateInput): boolean {
  if (!givenISODate || !other) {
    return false;
  }
  const givenMoment = moment(givenISODate);
  const otherMoment = moment(other);
  return givenMoment.isValid() && otherMoment.isValid() && otherMoment.isAfter(givenMoment);
}

/**
 * Checks if a given ISO date is after the current time
 * @param givenISODate - The ISO date string to check
 * @returns True if the given date is in the future
 */
export function givenTimeIsAfterCurrent(givenISODate: DateInput): boolean {
  if (!givenISODate) {
    return false;
  }
  const givenMoment = moment(givenISODate);
  return givenMoment.isValid() && givenMoment.isAfter(getESTNow());
}

/**
 * Returns the latest minimum date between a given date and now
 * @param givenMinDate - The minimum date to check
 * @returns A moment object representing the latest of the two dates
 */
export function getLatestMinDate(givenMinDate: DateInput): Moment {
  return givenTimeIsAfterCurrent(givenMinDate)
    ? moment(givenMinDate)
    : getESTNow();
}

/**
 * Converts an ISO date/time to various formatted strings in different timezones
 * @param ISODateTime - The ISO date/time string to convert
 * @param timeZone - The target timezone (defaults to window.timeZone or store config)
 * @returns An object containing various formatted date/time strings
 */
export function convertISODateTime(
  ISODateTime: DateInput,
  timeZone?: string
): ConvertedISODateTime {
  if (!ISODateTime || !moment(ISODateTime).isValid()) {
    return {
      toMomentWithConsoleConfigTimeZone: null,
      toMomentWithDBTimeZone: null,
      toWordDate: "",
      toDateTimeString: "",
      toDate: "",
      toTime: "",
      toTimeSpace: "",
      toTimeZone: "",
      toDBDateTimeString: "",
      toDBDateString: "",
      toDBDateDashString: "",
      toDateTime: null,
    };
  }

  const tz = resolveTimeZone(timeZone);
  const convertedMomentConsoleConfigTimeZone = moment.tz(ISODateTime, tz);
  const convertedMomentDBTimeZone = moment.tz(ISODateTime, "America/New_York");

  return {
    toMomentWithConsoleConfigTimeZone: convertedMomentConsoleConfigTimeZone,
    toMomentWithDBTimeZone: convertedMomentDBTimeZone,
    toWordDate: convertedMomentConsoleConfigTimeZone.format("MMM. D, YYYY"),
    toDateTimeString: convertedMomentConsoleConfigTimeZone.format("YYYY-MM-DDTHH:mm:ss"),
    toDate: convertedMomentConsoleConfigTimeZone.format("MM/DD/YYYY"),
    toTime: convertedMomentConsoleConfigTimeZone.format("h:mmA"),
    toTimeSpace: convertedMomentConsoleConfigTimeZone.format("h:mm A"),
    toTimeZone: convertedMomentConsoleConfigTimeZone.format("z"),
    toDBDateTimeString: convertedMomentDBTimeZone.format("YYYY-MM-DDTHH:mm:ss"),
    toDBDateString: convertedMomentDBTimeZone
      .clone()
      .startOf("day")
      .format("YYYY-MM-DDTHH:mm:ss"),
    toDBDateDashString: convertedMomentDBTimeZone.format("YYYY-MM-DD"),
    toDateTime: convertedMomentConsoleConfigTimeZone.toDate(),
  };
}

/**
 * Converts a NYC datetime string (without timezone) to UTC ISO format
 * Used when we need a string without a timezone to be converted to UTC
 * @param string - The date/time string in format "YYYY-MM-DDTHH:mm:ss"
 * @returns A Date object in UTC or null if invalid
 */
export function convertNYCDateTimeStringToISO(string: string | null | undefined): Date | null {
  if (!string) {
    return null;
  }
  // because this is a string instead of an ISO date, you can assign a timezone to it
  return moment(string, "YYYY-MM-DDTHH:mm:ss").isValid()
    ? moment
        .tz(string, "YYYY-MM-DDTHH:mm:ss", "America/New_York")
        .tz("UTC")
        .toDate()
    : null;
}

/**
 * Converts a date/time string in a specific format and timezone to UTC ISO format
 * @param string - The date/time string to convert
 * @param format - The format of the input string (default: "MM/DD/YYYYh:mmA")
 * @param timeZoneAbbr - The target timezone abbreviation (default: "UTC")
 * @param timeZone - The source timezone (defaults to window.timeZone or store config)
 * @returns A Date object in the target timezone
 */
export function convertDefaultTimeZoneStringToISO(
  string: string,
  format: string = "MM/DD/YYYYh:mmA",
  timeZoneAbbr: string = "UTC",
  timeZone?: string
): Date {
  const tz = resolveTimeZone(timeZone);
  return moment.tz(string, format, tz).tz(timeZoneAbbr).toDate();
}

/**
 * Converts a date-only string (MM-DD-YYYY) to ISO DateTime in UTC
 * Used in promotions to convert date 03-03-17 to ISO DateTime
 * @param string - The date string in format "MM-DD-YYYY"
 * @returns A Date object in UTC or null if invalid
 */
export function convertDateOnlyToISO(string: string | null | undefined): Date | null {
  if (!string) {
    return null;
  }
  return moment(string, "MM-DD-YYYY").isValid()
    ? moment.tz(string, "MM-DD-YYYY", "America/New_York").tz("UTC").toDate()
    : null;
}

/**
 * Converts a date-only string (MM/DD/YYYY) to a Moment object in the console config timezone
 * @param string - The date string in format "MM/DD/YYYY"
 * @param timeZone - The target timezone (defaults to window.timeZone or store config)
 * @returns A Moment object or null if invalid
 */
export function convertDateOnlyToMomentWithConsoleConfigTimeZone(
  string: string | null | undefined,
  timeZone?: string
): Moment | null {
  if (!string) {
    return null;
  }
  const tz = resolveTimeZone(timeZone);
  return moment(string, "MM/DD/YYYY").isValid()
    ? moment.tz(string, "MM/DD/YYYY", tz)
    : null;
}

/**
 * Converts a date/time string (MM/DD/YYYYhh:mmA) to a Moment object in the console config timezone
 * @param string - The date/time string in format "MM/DD/YYYYhh:mmA"
 * @param timeZone - The target timezone (defaults to window.timeZone or store config)
 * @returns A Moment object or null if invalid
 */
export function convertDateTimeToMomentWithConsoleConfigTimeZone(
  string: string | null | undefined,
  timeZone?: string
): Moment | null {
  if (!string) {
    return null;
  }
  const tz = resolveTimeZone(timeZone);
  return moment(string, "MM/DD/YYYYhh:mmA").isValid()
    ? moment.tz(string, "MM/DD/YYYYhh:mmA", tz)
    : null;
}

/**
 * Handles timezone override logic for date/time strings
 * @param override - The timezone override type ('client', 'user', or undefined)
 * @param dateString - The date string
 * @param timeString - The time string
 * @returns An object with 'live' and 'iso' date/time representations
 */
export function handleTimeZoneOverride(
  override: TimeZoneOverride,
  dateString: string,
  timeString: string
): TimeZoneOverrideResult {
  const combinedString = dateString + timeString;

  switch (override) {
    case "client": {
      const momentObj = convertDateTimeToMomentWithConsoleConfigTimeZone(combinedString);
      return {
        live: momentObj ? momentObj.format("YYYY-MM-DDTHH:mm:ss") : null,
        iso: convertDefaultTimeZoneStringToISO(combinedString),
      };
    }
    case "user":
    default:
      // Default to user's local timezone
      return {
        live: formatDateTime(
          convertDefaultTimeZoneStringToISO(combinedString),
          "YYYY-MM-DDTHH:mm:ss"
        ),
        iso: convertDefaultTimeZoneStringToISO(combinedString),
      };
  }
}

/**
 * Converts a local date-only string to UTC ISO format
 * @param string - The date string in format "MM-DD-YYYY"
 * @param timeZone - The source timezone (defaults to window.timeZone or store config)
 * @returns A Date object in UTC or null if invalid
 */
export function convertLocalDateOnlyToISO(
  string: string | null | undefined,
  timeZone?: string
): Date | null {
  if (!string) {
    return null;
  }
  const tz = resolveTimeZone(timeZone);
  return moment(string, "MM-DD-YYYY").isValid()
    ? moment.tz(string, "MM-DD-YYYY", tz).tz("UTC").toDate()
    : null;
}

/**
 * Gets the current date/time in ISO format, optionally in a specific timezone and format
 * @param timeZone - The timezone to use (optional)
 * @param format - The format string (optional)
 * @returns The current date/time as a Date object, formatted string, or moment internal value
 */
export function getNowISO(timeZone?: string, format?: string): Date | string | any {
  const currentDate = getESTNow().toDate();
  const formattedDate = format ? moment(currentDate).format(format) : currentDate;
  return timeZone ? (moment(formattedDate).tz(timeZone) as any)._i : formattedDate;
}

/**
 * Converts a date string to a specific timezone
 * @param dateString - The date string or Date object to convert
 * @param timeZone - The target timezone (defaults to window.timeZone or store config)
 * @returns A Date object in the target timezone or null if invalid
 */
export function getTimeZoneConvertedDateTime(
  dateString: Date | string | null | undefined,
  timeZone?: string
): Date | null {
  if (!dateString) {
    return null;
  }
  const tz = resolveTimeZone(timeZone);
  const dateStr = typeof dateString !== "string" ? dateString.toISOString() : dateString;
  return dateStr ? (moment(dateStr).tz(tz) as any)._d : null;
}

/**
 * Validates if a string is a valid time in format "h:mm A"
 * @param str - The time string to validate
 * @returns True if the string is a valid time
 */
export function isValidTime(str: string): boolean {
  // true sets the parser to strict
  return moment(str, "h:mm A", true).isValid();
}

/**
 * Gets the current machine time in UTC, NYC, and DB timezones
 * @param timeZone - The DB timezone to use (defaults to window.timeZone or store config)
 * @returns An object with current times in different timezones
 */
export function getMachineNow(timeZone?: string): MachineNow {
  const tz = resolveTimeZone(timeZone);
  return {
    UTC: moment.tz().format("MM/DD/YYYY hh:mm:ss z"),
    NYC: moment.tz("America/New_York").format("MM/DD/YYYY hh:mm:ss z"),
    DB: moment.tz(tz).format("MM/DD/YYYY hh:mm:ss z"),
  };
}

/**
 * Adds dashes to a date string in format YYYYMMDD
 * @param dateStr - The date string without dashes (e.g., "20230315")
 * @returns The formatted date string (e.g., "2023-03-15") or empty string if invalid
 */
export function addDashesToDate(dateStr: string | null | undefined): string {
  if (!dateStr || dateStr.length !== 8 || isNaN(Number(dateStr))) {
    return "";
  }
  return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`;
}

/**
 * Adds dashes to a month string in format YYYYMM
 * @param dateStr - The month string without dashes (e.g., "202303")
 * @returns The formatted month string (e.g., "2023-03") or empty string if invalid
 */
export function addDashesToMonth(dateStr: string | null | undefined): string {
  if (!dateStr || dateStr.length !== 6 || isNaN(Number(dateStr))) {
    return "";
  }
  return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}`;
}

/**
 * Converts a date string to ISO week format
 * @param dateStr - The date string in format YYYYWW (e.g., "202312")
 * @returns The ISO week format string (e.g., "2023-W12") or empty string if invalid
 */
export function getMomentISOWeekFormat(dateStr: string | null | undefined): string {
  if (!dateStr || dateStr.length !== 6 || isNaN(Number(dateStr))) {
    return "";
  }
  return `${dateStr.slice(0, 4)}-W${dateStr.slice(4, 6)}`;
}

/**
 * Converts military time format to a beautified format
 * @param dateStr - The date/time string in format "YYYY-MM-DD HH:mm:ss"
 * @param timeZone - The target timezone (defaults to window.timeZone or store config)
 * @returns An object with formatted datetime and timezone abbreviation, or empty string if invalid
 */
export function beautifyMilitaryTime(
  dateStr: string | null | undefined,
  timeZone?: string
): BeautifiedTime | string {
  if (!dateStr) {
    return "";
  }
  const tz = resolveTimeZone(timeZone);
  
  if (!moment(dateStr, "YYYY-MM-DD HH:mm:ss").isValid()) {
    return "";
  }

  const momentTime = moment(dateStr, "YYYY-MM-DD HH:mm:ss").tz(tz);
  return {
    datetime: momentTime.format("M/D/YY h:mmA"),
    tz: momentTime.format("z"),
  };
}

/**
 * Rounds a date/time to the next time interval
 * @param dateTime - The date/time to round
 * @param interval - The interval in minutes to round to
 * @returns A Moment object rounded to the next interval
 * @example roundToInterval('2023-03-15 14:37:00', 15) // rounds to 14:45:00
 */
export function roundToInterval(dateTime: DateInput, interval: number): Moment {
  const givenTime = moment(dateTime);
  const timeToNextInterval = interval - (givenTime.minute() % interval);
  return givenTime.add(timeToNextInterval, "minutes");
}

/**
 * Gets the current timestamp in milliseconds
 * @returns The current timestamp as a number
 */
export function getTimestamp(): number {
  return +new Date();
}

/**
 * Calculates the difference between two dates
 * The supported measurements are years, months, weeks, days, hours, minutes, and seconds
 * @param dateStr - The first date string
 * @param dateStrAfter - The second date string (defaults to current time)
 * @param duration - The unit of measurement (default: "days")
 * @returns The difference between the two dates in the specified unit
 */
export function getDateDifference(
  dateStr: DateInput,
  dateStrAfter: DateInput = getESTNow() as any,
  duration: DurationUnit = "days"
): number {
  return moment(dateStrAfter).diff(moment(dateStr), duration);
}

/**
 * Gets the next day at start of day in a specific timezone
 * @param timeZone - The timezone to use (default: "America/New_York")
 * @param format - The format string (default: "YYYY-MM-DDTHH:mm:ss")
 * @returns The formatted date/time string for the next day
 */
export function getNextDay(
  timeZone: string = EST_TIMEZONE,
  format: string = "YYYY-MM-DDTHH:mm:ss"
): string {
  return getESTNow().add(1, "days").startOf("day").tz(timeZone).format(format);
}
